"use client";

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client';
import { useMutation } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Users, Mail, UserPlus, Trash2, Database, Zap, Shield, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { showSuccess, showError } from '@/utils/toast';
import { InviteForm } from '@/components/InviteForm';
import { InviteList } from '@/components/InviteList';
import { Skeleton } from '@/components/ui/skeleton';

const GodMode = () => {
  const { profile, isSuperAdmin } = useAuth();
  const [isResetting, setIsResetting] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [targetUserId, setTargetUserId] = useState('');

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Superadmin access required.</AlertDescription>
        </Alert>
      </div>
    );
  }

  // Reset database mutation (dangerous!)
  const resetMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('reset-data', {
        body: { action: 'full_reset', confirm: true },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Reset failed');
      return data;
    },
    onSuccess: (result) => {
      showSuccess(`Database reset complete. Backup: ${result.backup_url}`);
    },
    onError: (error: any) => {
      showError(error.message || 'Reset failed - operation cancelled');
    },
  });

  // Upgrade user to superadmin mutation
  const upgradeMutation = useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.functions.invoke('upgrade-to-superadmin', {
        body: { user_id: userId },
      });
      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || 'Upgrade failed');
      return data;
    },
    onSuccess: () => {
      showSuccess('User upgraded to superadmin!');
      setTargetUserId(''); // Clear input
    },
    onError: (error: any) => {
      showError(error.message || 'Upgrade failed');
    },
  });

  const handleUpgrade = () => {
    if (!targetUserId) {
      showError('Enter user ID first');
      return;
    }
    if (!confirm('Are you sure? This grants full system access.')) return;
    upgradeMutation.mutate(targetUserId);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold">God Mode (Superadmin)</h1>
      <Alert variant="destructive" className="mb-6">
        <AlertDescription className="flex items-center space-x-2">
          <AlertCircle className="h-4 w-4" />
          <span>⚠️ DANGER ZONE: These actions are irreversible. Use with extreme caution.</span>
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Database Controls</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="font-medium">Reset Database</div>
            <p className="text-sm text-muted-foreground">
              ⚠️ Permanently deletes all data. Creates backup first. Use only for testing.
            </p>
            <Button 
              variant="destructive" 
              onClick={() => resetMutation.mutate()} 
              disabled={resetMutation.isPending}
              className="w-full"
            >
              {resetMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetting Database...
                </>
              ) : (
                <>
                  <Database className="h-4 w-4 mr-2" />
                  Reset Database (⚠️ Dangerous!)
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="font-medium">Upgrade User to Superadmin</div>
            <p className="text-sm text-muted-foreground">
              Enter user ID to grant full system access. This bypasses all RLS policies.
            </p>
            <div className="flex space-x-2">
              <Input
                placeholder="User ID (UUID)"
                value={targetUserId}
                onChange={(e) => setTargetUserId(e.target.value)}
                className="flex-1"
              />
              <Button 
                variant="destructive" 
                onClick={handleUpgrade} 
                disabled={!targetUserId || upgradeMutation.isPending}
              >
                {upgradeMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Upgrading...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Upgrade User
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>System Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-2">
              <Shield className="h-4 w-4 text-green-500" />
              <div>
                <div className="font-medium">Superadmin Access</div>
                <div className="text-sm text-muted-foreground">Full system privileges active</div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Database className="h-4 w-4 text-blue-500" />
              <div>
                <div className="font-medium">Database</div>
                <div className="text-sm text-muted-foreground">Production database connected</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Recent Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span>User upgraded to superadmin</span>
            <span className="text-muted-foreground">2 hours ago</span>
          </div>
          <div className="flex justify-between">
            <span>Database backup created</span>
            <span className="text-muted-foreground">1 day ago</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GodMode;